import { SimNPC, isCardForbidden, getTargetTileForSeeker } from '../types/SimNPC';
import { SimWorld, addLog, isGroupModeEnabled } from '../types/SimWorld';
import { areGoodRelationship } from './RelationshipManager';
import { MUTATION_CONSTANTS } from './MutationManager';
import type { CardPoolConfig } from '../utils/CardFilter';
import type { CardWithId } from '../../types/card';

/**
 * NPCのグループのカードプール設定を取得
 */
function getNpcCardPoolConfig(npc: SimNPC, world: SimWorld): CardPoolConfig | undefined {
  if (isGroupModeEnabled(world) && npc.group && world.groupModeConfig) {
    const groupConfig = world.groupModeConfig.groups.find(g => g.id === npc.group);
    if (groupConfig) {
      return groupConfig.cardPoolConfig;
    }
  }
  return world.cardPoolConfig;
}

/**
 * カードがNPCのグループのカードプールで許可されているかチェック
 */
function isCardAllowedInNpcPool(card: CardWithId, npc: SimNPC, world: SimWorld): boolean {
  if (!isGroupModeEnabled(world)) {
    return true; // グループモードでなければ制限なし
  }

  const cardPoolConfig = getNpcCardPoolConfig(npc, world);
  if (!cardPoolConfig) {
    return true; // 設定がなければ制限なし
  }

  // 許可カードIDチェック
  if (cardPoolConfig.allowedCardIds && cardPoolConfig.allowedCardIds.size > 0) {
    if (!cardPoolConfig.allowedCardIds.has(card.id)) {
      return false;
    }
  }

  // 除外カードIDチェック
  if (cardPoolConfig.excludedCardIds?.has(card.id)) {
    return false;
  }

  // 属性制限チェック
  if (cardPoolConfig.allowedAttributes && cardPoolConfig.allowedAttributes.length > 0) {
    if (!cardPoolConfig.allowedAttributes.includes(card.attribute || 'なし')) {
      return false;
    }
  }

  // コスト制限チェック
  if (cardPoolConfig.maxCost !== undefined && card.cost > cardPoolConfig.maxCost) {
    return false;
  }

  return true;
}

/**
 * カード交換システム定数
 */
export const EXCHANGE_CONSTANTS = {
  MIN_AFFECTION: 40, // 交換に必要な最低好感度（友人以上）
  EXCHANGE_CHANCE: 0.1, // 交換発生確率（10%/日）
  MAX_SAME_NAME_CARDS: 3, // 同名カード最大枚数
  MAX_FORBIDDEN_CARDS: 1, // 禁忌カード最大枚数
};

/**
 * 属性リスト
 */
const ATTRIBUTES = ['炎', '水', '光', '闇', '原始', '未来'] as const;

/**
 * NPCの主属性を取得
 * シーカーならその属性、それ以外はデッキ内最多属性
 */
export function getPrimaryAttribute(npc: SimNPC): string | null {
  // シーカーの場合は固定属性を返す
  const seekerTarget = getTargetTileForSeeker(npc.movementType);
  if (seekerTarget) {
    // fire_zone -> 炎 のマッピング
    switch (seekerTarget) {
      case 'fire_zone': return '炎';
      case 'water_zone': return '水';
      case 'light_zone': return '光';
      case 'dark_zone': return '闇';
      case 'primitive_zone': return '原始';
      case 'future_zone': return '未来';
    }
  }

  // デッキ内枚数で判定
  const counts: Record<string, number> = {};
  ATTRIBUTES.forEach(attr => counts[attr] = 0);

  for (const card of npc.deck) {
    if (card.attribute && counts[card.attribute] !== undefined) {
      counts[card.attribute]++;
    }
  }

  // 最大枚数の属性を取得
  let maxAttr: string | null = null;
  let maxCount = 0;
  for (const [attr, count] of Object.entries(counts)) {
    if (count > maxCount) {
      maxCount = count;
      maxAttr = attr;
    }
  }

  return maxAttr;
}

/**
 * カードを受け取れるかチェック（デッキ制限）
 * - 同名カード3枚まで
 * - 禁忌カード1枚まで
 */
export function canReceiveCard(npc: SimNPC, card: CardWithId): boolean {
  // 同名カードのカウント
  const sameNameCount = npc.deck.filter(c => c.id === card.id).length;
  if (sameNameCount >= EXCHANGE_CONSTANTS.MAX_SAME_NAME_CARDS) {
    return false;
  }

  // 禁忌カードの場合、既存の禁忌カード数をチェック
  if (isCardForbidden(card)) {
    const forbiddenCount = npc.deck.filter(c => isCardForbidden(c)).length;
    if (forbiddenCount >= EXCHANGE_CONSTANTS.MAX_FORBIDDEN_CARDS) {
      return false;
    }
  }

  return true;
}

/**
 * 交換候補カードを選択
 * 優先順位：
 * 1. 属性ミスマッチカード（主属性と異なる）
 * 2. 勝率の低いカード
 */
function selectExchangeCandidate(npc: SimNPC): number | null {
  const primaryAttr = getPrimaryAttribute(npc);
  const minGames = MUTATION_CONSTANTS.MIN_GAMES_FOR_EVALUATION;

  // 候補をスコア付け（低いほど交換対象になりやすい）
  const candidates: { index: number; score: number }[] = [];

  for (let i = 0; i < npc.deck.length; i++) {
    const card = npc.deck[i];
    let score = 0.5; // 基本スコア

    // 属性ミスマッチならスコアを下げる（交換しやすい）
    if (primaryAttr && card.attribute && card.attribute !== primaryAttr) {
      score -= 0.3;
    }

    // 勝率統計があればそれを考慮
    const stat = npc.cardStats.get(card.id);
    if (stat && stat.gamesWithCard >= minGames) {
      const winRate = stat.winsWithCard / stat.gamesWithCard;
      score += winRate * 0.3; // 勝率が高いほどスコアが上がる（交換されにくい）
    }

    candidates.push({ index: i, score });
  }

  // スコアが低い順にソート
  candidates.sort((a, b) => a.score - b.score);

  // 最もスコアが低いカードを返す
  if (candidates.length > 0) {
    return candidates[0].index;
  }

  return null;
}

/**
 * 2人のNPC間でカード交換を試行
 * @returns 交換が成立したかどうか
 */
export function tryCardExchange(
  npc1: SimNPC,
  npc2: SimNPC,
  world: SimWorld
): boolean {
  // 好感度チェック（双方が友人以上）
  if (!areGoodRelationship(npc1, npc2, EXCHANGE_CONSTANTS.MIN_AFFECTION)) {
    return false;
  }

  // 交換発生確率チェック
  if (Math.random() >= EXCHANGE_CONSTANTS.EXCHANGE_CHANCE) {
    return false;
  }

  // 各NPCの交換候補を選択
  const index1 = selectExchangeCandidate(npc1);
  const index2 = selectExchangeCandidate(npc2);

  if (index1 === null || index2 === null) {
    return false;
  }

  const card1 = npc1.deck[index1];
  const card2 = npc2.deck[index2];

  // 同じカードなら交換しない
  if (card1.id === card2.id) {
    return false;
  }

  // 受け取り可能かチェック
  if (!canReceiveCard(npc1, card2) || !canReceiveCard(npc2, card1)) {
    return false;
  }

  // グループモード時: 受け取るカードが自分のグループで許可されているかチェック
  if (!isCardAllowedInNpcPool(card2, npc1, world) || !isCardAllowedInNpcPool(card1, npc2, world)) {
    return false;
  }

  // 同じカードタイプでないと交換しない（モンスター同士、魔法同士など）
  if (card1.type !== card2.type) {
    return false;
  }

  // カード交換実行
  npc1.deck[index1] = {
    ...card2,
    uniqueId: `${card2.id}_exchange_${Date.now()}_${Math.random()}`,
  };
  npc2.deck[index2] = {
    ...card1,
    uniqueId: `${card1.id}_exchange_${Date.now()}_${Math.random()}`,
  };

  // ログ出力
  addLog(
    world,
    `🔄 ${npc1.name}と${npc2.name}がカード交換！${card1.name} ⇔ ${card2.name}`
  );

  return true;
}

/**
 * 同じマスにいるNPC同士のカード交換を処理
 */
export function processCardExchanges(
  npcsOnSameTile: SimNPC[],
  world: SimWorld
): void {
  if (npcsOnSameTile.length < 2) {
    return;
  }

  // 全ペアで交換を試行
  for (let i = 0; i < npcsOnSameTile.length; i++) {
    for (let j = i + 1; j < npcsOnSameTile.length; j++) {
      const npc1 = npcsOnSameTile[i];
      const npc2 = npcsOnSameTile[j];

      if (npc1.isAlive && npc2.isAlive) {
        tryCardExchange(npc1, npc2, world);
      }
    }
  }
}
